/**
 * Admin Manual — operator handbook for CaiT Academy admins.
 *
 * Self-gating: /api/auth/me is called on mount; non-admin (or
 * unauthenticated) viewers see a "admin access required" notice
 * instead of the manual. The manual content itself isn't sensitive
 * (mostly UI walkthroughs + ops procedures), but gating keeps the
 * surface area predictable and matches AdminDashboardPage's pattern.
 */
import type * as React from "react";
import { useEffect, useState } from "react";

import { API_BASE_URL, ApiError, apiGet } from "@/lib/api";

type AuthState =
  | { kind: "loading" }
  | { kind: "admin" }
  | { kind: "not-admin" }
  | { kind: "unauthenticated" };

function redirectToLogin(): void {
  window.location.href = `${API_BASE_URL}/api/auth/login?return_to=${encodeURIComponent("/admin/handbook")}`;
}

export function AdminManualPage(): React.JSX.Element {
  const [auth, setAuth] = useState<AuthState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    void apiGet<{ role: string }>("/api/auth/me")
      .then((me) => {
        if (cancelled) return;
        if (me.role === "admin") {
          setAuth({ kind: "admin" });
        } else {
          setAuth({ kind: "not-admin" });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          setAuth({ kind: "unauthenticated" });
          return;
        }
        setAuth({ kind: "not-admin" });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (auth.kind === "loading") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6" aria-busy="true">
        <p className="text-sm text-muted-foreground" role="status">
          Loading…
        </p>
      </section>
    );
  }

  if (auth.kind === "unauthenticated") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <h1 className="text-2xl font-bold text-foreground">
          Admin handbook
        </h1>
        <p className="mt-4 text-sm text-foreground">
          You need to sign in first.{" "}
          <button
            type="button"
            onClick={redirectToLogin}
            className="text-primary underline hover:no-underline"
          >
            Sign in
          </button>
          .
        </p>
      </section>
    );
  }

  if (auth.kind === "not-admin") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <h1 className="text-2xl font-bold text-foreground">
          Admin handbook
        </h1>
        <p className="mt-4 text-sm text-amber-900">
          This page is for CaiT Academy admins. If you think you should
          have admin access, contact{" "}
          <a
            href="mailto:support@clinicianassist.ai"
            className="text-primary underline hover:no-underline"
          >
            support@clinicianassist.ai
          </a>
          .
        </p>
      </section>
    );
  }

  // auth.kind === "admin"
  return (
    <article className="prose mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Admin handbook
      </p>
      <h1 className="mt-2 text-3xl font-bold text-foreground">
        CaiT Academy — admin operator manual
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Last updated 2026-06-02. Everything an admin needs to invite
        learners, read the dashboard, grade capstones, and recover from
        common operational hiccups.
      </p>

      {/* ===== TOC ===== */}
      <nav
        aria-label="On this page"
        className="mt-8 rounded-lg border border-border bg-muted/40 p-4 text-sm"
      >
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          On this page
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-foreground">
          <li><a href="#dashboard" className="hover:text-primary">Reading the learner dashboard</a></li>
          <li><a href="#inviting" className="hover:text-primary">Inviting a new learner</a></li>
          <li><a href="#at-risk" className="hover:text-primary">At-risk flags + nightly digest</a></li>
          <li><a href="#learner-detail" className="hover:text-primary">Learner detail page</a></li>
          <li><a href="#grading" className="hover:text-primary">Grading the capstone queue</a></li>
          <li><a href="#cert" className="hover:text-primary">Certificate issuance</a></li>
          <li><a href="#gamification" className="hover:text-primary">Badges + XP columns</a></li>
          <li><a href="#troubleshooting" className="hover:text-primary">Troubleshooting + recovery</a></li>
          <li><a href="#data-rules" className="hover:text-primary">Data rules + privacy</a></li>
        </ul>
      </nav>

      {/* ===== DASHBOARD ===== */}
      <section id="dashboard" className="mt-10 space-y-4 text-foreground">
        <h2 className="text-xl font-semibold text-foreground">
          Reading the learner dashboard
        </h2>
        <p>
          The <a href="/admin" className="text-primary hover:underline">Learner
          dashboard</a> is the single screen you land on. Four summary tiles
          at top:
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li><strong>LEARNERS</strong>: total enrollments (any status).</li>
          <li><strong>ACTIVE</strong>: enrollment_status = active (first sign-in completed).</li>
          <li><strong>AT RISK</strong>: count of learners with at-risk flags. Click <em>At risk</em> on the table tabs to filter.</li>
          <li><strong>AWAITING GRADING</strong>: count of submitted capstones not yet graded — your work queue.</li>
        </ul>
        <p>The roster table below has these columns:</p>
        <ul className="ml-6 list-disc space-y-1">
          <li><strong>LEARNER</strong>: display name + email (the email is the source-of-truth identifier).</li>
          <li><strong>STATUS</strong>: <code>pending</code> (invited, not yet signed in) / <code>active</code> / inactive states. Plus a <em>not signed in</em> sub-label until first WorkOS login.</li>
          <li><strong>LESSONS</strong>: completed / total (max 50).</li>
          <li><strong>MODULES</strong>: quiz-passed / total (max 10).</li>
          <li><strong>EXAM</strong>: not started / in progress / passed / failed.</li>
          <li><strong>CAPSTONE</strong>: not started / submitted / graded.</li>
          <li><strong>CERT</strong>: ✓ if a certificate was issued; — otherwise.</li>
          <li><strong>BADGES</strong>: count of gamification badges earned.</li>
          <li><strong>XP</strong>: total XP from lesson + quiz + exam + capstone + streak awards.</li>
          <li><strong>LAST ACTIVE</strong>: most recent heartbeat or sign-in.</li>
          <li><strong>FLAGS</strong>: at-risk badges (see below).</li>
        </ul>
        <p>
          Click any row to open the learner detail page (see <a href="#learner-detail" className="text-primary hover:underline">below</a>).
        </p>
      </section>

      {/* ===== INVITING ===== */}
      <section id="inviting" className="mt-10 space-y-4 text-foreground">
        <h2 className="text-xl font-semibold text-foreground">
          Inviting a new learner
        </h2>
        <p>
          Use the <strong>Create a new learner</strong> card on the
          dashboard. Fields:
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li><strong>Email</strong> (required): the email the welcome message goes to AND the identity the learner signs in with.</li>
          <li><strong>Name</strong> (optional): seed display name. Note: WorkOS will overwrite this on first sign-in with whatever first/last name the learner enters on the sign-up form, so treat your input as a placeholder.</li>
          <li><strong>Course version</strong>: defaults to the active version (currently the only active option).</li>
          <li><strong>Price tier</strong>: defaults to <em>Founding Cohort (comped)</em>. All Phase 0 enrollments are comped — there is no Stripe path live yet.</li>
        </ul>
        <p>
          Clicking <em>Create learner</em> does three things in a single
          transaction:
        </p>
        <ol className="ml-6 list-decimal space-y-1">
          <li>Creates (or finds) a <code>users</code> row by normalized email.</li>
          <li>Creates an <code>enrollments</code> row with <code>status=pending</code>, <code>amount_paid_cents=0</code>.</li>
          <li>Queues a welcome email (template <code>welcome</code>) with the Start-Module-1 CTA and a link to the Learner handbook.</li>
        </ol>
        <p>
          The enrollment flips <code>pending → active</code>{" "}
          automatically the first time the learner signs in via WorkOS.
          Until then, the dashboard shows <em>pending · not signed in</em>{" "}
          and the at-risk system will eventually flag them (see below).
        </p>
        <p>
          If the email already has an active enrollment for this course
          version, you&apos;ll get a 409 with the message{" "}
          <em>&quot;...already has an active enrollment...&quot;</em> — refund
          or expire the prior enrollment before re-creating.
        </p>
      </section>

      {/* ===== AT-RISK ===== */}
      <section id="at-risk" className="mt-10 space-y-4 text-foreground">
        <h2 className="text-xl font-semibold text-foreground">
          At-risk flags + nightly digest
        </h2>
        <p>
          At-risk flags appear in the FLAGS column and surface in the
          dashboard&apos;s AT RISK tile. Current rules:
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li><strong>Never signed in</strong>: pending enrollment that&apos;s sat unactivated for 7+ days.</li>
          <li><strong>Stalled</strong>: active learner with no heartbeat in 14+ days who hasn&apos;t finished.</li>
          <li><strong>Awaiting grading</strong>: capstone submitted but not graded for 2+ days.</li>
          <li><strong>Email bouncing</strong>: most recent email delivery returned a hard bounce.</li>
        </ul>
        <p>
          A <strong>nightly digest</strong> emails every admin the
          full at-risk list (if any). It uses the same rules as the
          in-app FLAGS column — one source of truth in{" "}
          <code className="text-xs">backend/caitapi/services/learner_progress.py</code>.
          If 0 learners are flagged, the digest sends nothing.
        </p>
        <p>
          Click the <em>At risk</em> tab on the roster table to filter
          to just the flagged subset.
        </p>
      </section>

      {/* ===== LEARNER DETAIL ===== */}
      <section id="learner-detail" className="mt-10 space-y-4 text-foreground">
        <h2 className="text-xl font-semibold text-foreground">
          Learner detail page
        </h2>
        <p>
          Click any row on the dashboard to open{" "}
          <code className="text-xs">/admin/learners/&lt;user_id&gt;</code>.
          Shows:
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>Identity + enrollment status banner</li>
          <li>Module-by-module breakdown (lessons completed, quiz pass + attempts)</li>
          <li>Final-exam attempt history with scores</li>
          <li>Capstone status with link to the submission</li>
          <li>Certificate status (issued? when? verify URL)</li>
        </ul>
        <p>
          This is the single page to send a confused learner&apos;s state
          to support without having to grant the learner support
          access. PII-light; safe to screenshot for internal triage
          (the email is shown but no clinical data is on the detail
          page itself — only progress metrics).
        </p>
      </section>

      {/* ===== GRADING ===== */}
      <section id="grading" className="mt-10 space-y-4 text-foreground">
        <h2 className="text-xl font-semibold text-foreground">
          Grading the capstone queue
        </h2>
        <p>
          From the dashboard, click <strong>Grading queue →</strong>{" "}
          (top-right) to open{" "}
          <code className="text-xs">/admin/capstones</code>. The queue
          lists submitted-but-ungraded capstones, default sort: oldest
          first.
        </p>
        <p>
          Click a row to open the grading detail panel. Trigger
          AI-grading (Claude Sonnet 4.6) with the <em>Grade with AI</em>{" "}
          button. The grader returns within ~30 seconds with:
        </p>
        <ul className="ml-6 list-disc space-y-1">
          <li>5 criterion scores (1–5 each): clinical reasoning, ethical application, technical understanding, case specificity, professional communication.</li>
          <li>Composite (unweighted average).</li>
          <li>Confidence band: <code>high</code> / <code>borderline</code> / <code>low</code>.</li>
          <li>Pass/fail (composite ≥4.0 AND every criterion ≥3).</li>
          <li>Narrative explaining the scoring.</li>
        </ul>
        <p>
          <strong>If pass + high confidence</strong>: the certificate
          issues automatically (see below) and a graded email fires to
          the candidate.
        </p>
        <p>
          <strong>If borderline</strong>: review the narrative and the
          essay yourself. The system flags borderline rows for human
          confirmation before the result reaches the candidate.
        </p>
        <p>
          <strong>If fail</strong>: the candidate gets a fail email with
          rubric feedback and the option to resubmit (they get 2 attempts
          per enrollment). Drafts don&apos;t count as attempts.
        </p>
      </section>

      {/* ===== CERT ===== */}
      <section id="cert" className="mt-10 space-y-4 text-foreground">
        <h2 className="text-xl font-semibold text-foreground">
          Certificate issuance
        </h2>
        <p>
          Auto-fires after a passing high-confidence capstone grade.
          The pipeline:
        </p>
        <ol className="ml-6 list-decimal space-y-1">
          <li>Render PDF + QR PNG via WeasyPrint</li>
          <li>Upload both to the <code>cait-academy-certs</code> R2 bucket</li>
          <li>Insert the <code>certificates</code> row (status=active)</li>
          <li>Email the candidate with the PDF URL + the verify URL</li>
        </ol>
        <p>
          Verify URL format:{" "}
          <code className="text-xs">https://verify.cait.academy/&lt;credential_id&gt;</code>
          . Cert validity: 3 years; recert reminders fire at 90, 30, 7
          days before expiry.
        </p>
        <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <strong>Known issue (deferred to production cutover):</strong>{" "}
          on test/demo, cert issuance is currently failing with{" "}
          <code>R2 SignatureDoesNotMatch</code>. The grade commits
          fine; only the PDF upload + cert row + email fail. Fix at
          production cutover is to rotate the{" "}
          <code>R2_SECRET_ACCESS_KEY</code> Fly secret. The admin queue
          will show <em>cert_issuance_error</em> on the affected
          capstone row when this happens.
        </p>
      </section>

      {/* ===== GAMIFICATION ===== */}
      <section id="gamification" className="mt-10 space-y-4 text-foreground">
        <h2 className="text-xl font-semibold text-foreground">
          Badges + XP columns
        </h2>
        <p>
          The BADGES + XP columns reflect each learner&apos;s
          gamification state. 28 badges in the Phase 0 catalog,
          XP awarded on lesson completion / module quiz pass / exam pass
          / capstone pass / streak milestones. Per DEC-0031,{" "}
          <strong>there is no historical backfill</strong> — learners
          who completed work before Sprint 7 will show 0 / 0 even if
          they finished the whole course. This is intentional.
        </p>
        <p>
          You can&apos;t award badges or XP from the admin UI (the engine
          is event-driven only). If a learner reports a missing award,
          check the events log first; if it&apos;s a legitimate miss,
          file an internal ticket — manual award is a maintenance
          script, not a self-serve admin action.
        </p>
      </section>

      {/* ===== TROUBLESHOOTING ===== */}
      <section id="troubleshooting" className="mt-10 space-y-4 text-foreground">
        <h2 className="text-xl font-semibold text-foreground">
          Troubleshooting + recovery
        </h2>

        <h3 className="mt-4 text-base font-semibold text-foreground">
          A learner says they&apos;re stuck mid-knowledge-check
        </h3>
        <p>
          The system handles this automatically as of 2026-06-02: any
          in-flight knowledge-check attempt is resumed on the next{" "}
          <em>Take the knowledge check</em> click, with prior answers
          pre-selected. The learner can also click <em>Reset attempt</em>{" "}
          to abandon and start fresh. No admin action needed.
        </p>

        <h3 className="mt-4 text-base font-semibold text-foreground">
          A learner says the welcome email never arrived
        </h3>
        <p>
          Check the dashboard row — status will still be{" "}
          <em>pending · not signed in</em> if they haven&apos;t signed in.
          Re-trigger by re-creating the enrollment? <strong>No.</strong>{" "}
          That 409s. Instead, ask Engineering to re-fire the welcome
          email via a one-off script; the user record is fine.
        </p>

        <h3 className="mt-4 text-base font-semibold text-foreground">
          A learner&apos;s exam attempt looks stuck mid-flight
        </h3>
        <p>
          An exam attempt with <code>status=in_progress</code> and
          <code>started_at</code> far in the past is fine — Phase 0
          doesn&apos;t enforce a hard timer. The learner can resume it
          by navigating back to the exam page. If they want to start
          over, the only way today is via an Engineering one-off
          (no abandon endpoint for exam attempts yet).
        </p>

        <h3 className="mt-4 text-base font-semibold text-foreground">
          A capstone grade looks wrong to me
        </h3>
        <p>
          The grader provides a per-criterion rationale; read it. If you
          still disagree, you can manually adjust the grade via an
          Engineering script. Avoid overriding without documenting why
          in the audit log — graded capstones with auto-issued certs
          are the hard-to-revoke side of this system.
        </p>

        <h3 className="mt-4 text-base font-semibold text-foreground">
          Dashboard says &quot;Loading...&quot; forever
        </h3>
        <p>
          Usually an expired admin session. Sign out + sign back in.
          If the API itself is down,{" "}
          <code>https://api.cait.academy/healthz</code> will return non-200;
          check Fly status if so.
        </p>
      </section>

      {/* ===== DATA RULES ===== */}
      <section id="data-rules" className="mt-10 space-y-4 text-foreground">
        <h2 className="text-xl font-semibold text-foreground">
          Data rules + privacy
        </h2>
        <ul className="ml-6 list-disc space-y-1">
          <li>
            <strong>PII handling</strong>: emails are the only PII
            field shown in the admin UI. No clinical content is stored
            (CaiT Academy is education, not clinical).
          </li>
          <li>
            <strong>Audit log</strong>: every admin action (create
            enrollment, grade capstone, issue cert, manual override) is
            recorded in <code>audit_log</code> with actor_user_id +
            timestamp + payload. Don&apos;t delete audit rows.
          </li>
          <li>
            <strong>Right to be forgotten</strong>: handled out-of-band
            via support@. Standard procedure is to anonymize the user
            record (replace name/email with hashes), preserve audit
            trail, and revoke any active certificate.
          </li>
        </ul>
        <p>
          See <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a> for
          the public-facing version of the data posture; this page is
          internal-only.
        </p>
      </section>
    </article>
  );
}
